import {
  cleanNumber,
  formatNumber,
  incrementNumber,
  parseFormattedNumber,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { Platform, StyleProp, TextStyle, ViewStyle } from 'react-native';
import { IconButton, TextInput } from 'react-native-paper';
import { NumericFormat } from 'react-number-format';
import { Colors } from '../../themes';
import {
  StyledTextInput,
  StyledTextInputProps,
  styledTextInputStyles,
} from './controlledTextInput';

type MaskedNumberInputProps = Omit<
  StyledTextInputProps,
  'onChangeText' | 'value'
> & {
  value: number | string | null;
  onChangeText: (value: number | string) => void;
  decimalPlaces?: number;
  mode?: 'outlined' | 'flat' | undefined;
  hideClear?: boolean;
  selectionColor?: string;
  cursorColor?: string;
  outlineStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<TextStyle>;
  showStepper?: boolean;
  disabled?: boolean;
};

export const MaskedNumberInput = ({
  value,
  onChangeText,
  onBlur,
  decimalPlaces = 0,
  mode = 'outlined',
  hideClear = false,
  ...rest
}: MaskedNumberInputProps) => {
  const [rawText, setRawText] = useState(
    !value || value === '0' ? undefined : value
  );
  const [initValue, setInitValue] = useState(
    !value || value === '0' ? undefined : value
  );

  const handleChange = (text: string, isIncrement?: boolean) => {
    const cleaned = isIncrement ? text : text.replace(/[^\d.,]/g, '');
    setRawText(cleaned);

    let parsed: string | number = cleaned;
    if (Platform.OS === 'web') {
      parsed = parseFormattedNumber(cleaned);
    }
    onChangeText(parsed);
    if (isIncrement) {
      const parsed = parseFormattedNumber(cleaned ?? '');
      setRawText(formatNumber(parsed, decimalPlaces));
    }
  };

  const handleBlur = () => {
    const parsed = parseFormattedNumber((rawText ?? '') as string);
    setRawText(formatNumber(parsed, decimalPlaces));
    onBlur?.();
  };

  const placeholder =
    decimalPlaces > 0 ? `0.${Array(decimalPlaces).fill('0').join('')}` : '0';

  if (Platform.OS === 'web') {
    return (
      <>
        {rest.showStepper && (
          <ShowStepperIconButton
            iconName="minus"
            onPress={() =>
              setInitValue(
                `${incrementNumber(value?.toString() ?? '', -1, decimalPlaces)}`
              )
            }
            isLeft={false}
            disabled={rest.disabled}
          />
        )}
        <NumericFormat
          style={[
            {
              height: rest.height || 55,
              width: rest.width || 'auto',
              backgroundColor: rest?.backgroundColor ?? Colors.white,
            },
            rest.outlinedTextInput && {
              borderWidth: 1,
              borderColor: Colors.tabInactive,
              borderRadius: 8,
            },
          ]}
          contentStyle={[
            rest.disableInnerStyle
              ? undefined
              : rest.textArea
              ? [
                  styledTextInputStyles.baseInputContent,
                  styledTextInputStyles.areaInputContent,
                ]
              : [styledTextInputStyles.baseInputContent],
            rest.inputInnerStyle,
          ]}
          {...rest}
          cursorColor={Colors.grayText}
          value={initValue}
          allowLeadingZeros
          decimalSeparator="."
          thousandSeparator=","
          decimalScale={decimalPlaces}
          fixedDecimalScale={false}
          customInput={TextInput}
          onValueChange={(vals) => {
            const { floatValue, value: rawStr } = vals as any;
            if (rawStr === '') {
              onChangeText('');
              return;
            }
            if (typeof floatValue === 'number') {
              onChangeText(floatValue);
              return;
            }
            onChangeText('');
          }}
          onBlur={onBlur}
          placeholder={placeholder}
          mode={mode}
          right={
            hideClear ? (
              <TextInput.Icon
                size={20}
                icon="close"
                onPress={() => onChangeText(0)}
              />
            ) : undefined
          }
          disabled={rest.disabled}
        />
        {rest.showStepper && (
          <ShowStepperIconButton
            iconName="plus"
            onPress={() =>
              setInitValue(
                `${incrementNumber(value?.toString() ?? '', +1, decimalPlaces)}`
              )
            }
            isLeft
            disabled={rest.disabled}
          />
        )}
      </>
    );
  }

  return (
    <>
      {rest.showStepper && (
        <ShowStepperIconButton
          iconName="minus"
          onPress={() =>
            handleChange(
              `${incrementNumber(
                `${cleanNumber(
                  (rawText ?? '') as string,
                  decimalPlaces,
                  true
                )}`,
                -1,
                decimalPlaces
              )}`,
              true
            )
          }
          isLeft={false}
          disabled={rest.disabled}
        />
      )}
      <StyledTextInput
        {...rest}
        value={rawText}
        onChangeText={handleChange}
        onBlur={handleBlur}
        onFocus={() =>
          setRawText(
            cleanNumber((rawText ?? '') as string, decimalPlaces, true)
          )
        }
        placeholder={placeholder}
        keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
        inputMode="decimal"
        disabled={rest.disabled}
      />
      {rest.showStepper && (
        <ShowStepperIconButton
          iconName="plus"
          onPress={() =>
            handleChange(
              `${incrementNumber(
                `${cleanNumber(
                  (rawText ?? '') as string,
                  decimalPlaces,
                  true
                )}`,
                +1,
                decimalPlaces
              )}`,
              true
            )
          }
          isLeft
          disabled={rest.disabled}
        />
      )}
    </>
  );
};

const ShowStepperIconButton = (props: {
  iconName: string;
  onPress: () => void;
  isLeft: boolean;
  disabled?: boolean;
}) => (
  <IconButton
    mode="outlined"
    icon={props.iconName}
    iconColor={Colors.grayText}
    size={24}
    style={[
      styledTextInputStyles.counterButton,
      props.isLeft
        ? { borderLeftWidth: 1, borderLeftColor: Colors.grayText }
        : { borderRightWidth: 1, borderRightColor: Colors.grayText },
    ]}
    onPress={props.onPress}
    disabled={props.disabled}
  />
);
